import React from "react";
import { ChevronDownIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";

const METHOD_COLOR = {
  GET: { bg: "#f0fdf4", text: "#16a34a" },
  POST: { bg: "#eff6ff", text: "#2563eb" },
  PUT: { bg: "#fffbeb", text: "#d97706" },
  PATCH: { bg: "#faf5ff", text: "#9333ea" },
  DELETE: { bg: "#fef2f2", text: "#dc2626" },
};

const ChainTestResults = ({ results, chainSteps = [] }) => {
  if (!results || results.length === 0) {
    return <div className="text-postman-gray-medium p-4">No results to display.</div>;
  }

  const uniq = [...new Set(results)];

  return (
    <div className="bg-white text-postman-gray-dark w-full font-apple">
      {/* Header */}
      <div className="bg-postman-gray-light px-4 py-3 border-b border-gray-200">
        <h2 className="text-sm font-medium">Chain Test Results</h2>
        <p className="text-xs text-postman-gray-medium mt-1">
          {uniq.length} step{uniq.length !== 1 ? "s" : ""} &middot; Generated on:{" "}
          {new Date().toLocaleString()}
        </p>
      </div>

      {/* Results */}
      <div className="divide-y divide-gray-200">
        {uniq?.map((item, i) => {
          const step = chainSteps[i];
          const methodStyle = METHOD_COLOR[step?.http_method] ?? { bg: "#f3f4f6", text: "#374151" };

          const allTests = item.testResults ? Object.entries(item.testResults) : [];
          const passCount = allTests.filter(([, v]) => v.startsWith("PASS")).length;
          const failCount = allTests.length - passCount;
          const allPassed = failCount === 0 && allTests.length > 0;

          if (item.testResults && item.originalResponse !== undefined) {
            return (
              <details key={`chain-step-${i}`} className="group">
                <summary className="flex items-center justify-between p-3 cursor-pointer list-none hover:bg-gray-50 select-none">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-200 text-gray-600 flex-shrink-0 tabular-nums">
                      {i + 1}
                    </span>

                    {step?.http_method && (
                      <span
                        className="text-[11px] font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{
                          backgroundColor: methodStyle.bg,
                          color: methodStyle.text,
                          fontFamily: "ui-monospace, monospace",
                        }}
                      >
                        {step.http_method}
                      </span>
                    )}

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate leading-tight">
                        {step?.test_case || step?.endpoint || "Chain Execution"}
                      </p>
                      {step?.test_case && (
                        <p className="text-[11px] font-mono text-gray-400 truncate leading-tight">
                          {step.endpoint}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {failCount > 0 && (
                        <span className="text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                          {failCount} fail
                        </span>
                      )}
                      <span
                        className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                          allPassed ? "text-green-700 bg-green-50" : "text-gray-500 bg-gray-100"
                        }`}
                      >
                        {passCount}/{allTests.length}
                      </span>
                    </div>
                  </div>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0 transition-transform group-open:rotate-180" />
                </summary>

                <div className="border-t border-gray-200 bg-gray-50">
                  <div className="p-3">
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Test Results</h4>
                      <div className="space-y-2">
                        {Object.entries(item.testResults).map(([testName, testResult]) => {
                          const isPassed = testResult.startsWith("PASS");
                          const description = testResult.slice(5).trim();
                          return (
                            <div
                              key={testName}
                              className="flex items-start justify-between p-2 bg-white rounded border gap-2"
                            >
                              <div className="flex items-center gap-2">
                                {isPassed ? (
                                  <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                                ) : (
                                  <XCircleIcon className="h-4 w-4 text-red-500 flex-shrink-0" />
                                )}
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{testName}</p>
                                  {description && (
                                    <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                                  )}
                                </div>
                              </div>
                              <span
                                className={`text-xs font-semibold px-2 py-1 rounded flex-shrink-0 ${
                                  isPassed
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {isPassed ? "PASS" : "FAIL"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Response Data</h4>
                      <pre className="bg-white font-mono p-3 rounded text-xs overflow-x-auto border border-gray-200">
                        {JSON.stringify(item.originalResponse, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              </details>
            );
          }

          return Object.entries(item).map(([path, testCases]) => (
            <details key={path} className="group">
              <summary className="flex items-center justify-between p-3 cursor-pointer list-none hover:bg-postman-gray-light select-none">
                <div className="flex items-center gap-2">
                  <div className="text-xs font-medium px-2 py-1 rounded bg-postman-orange text-white">
                    {path?.split("@")[1]?.split("/")[1] || "API"}
                  </div>
                  <span className="text-sm text-postman-gray-medium">{path?.split("@")[1]}</span>
                </div>
                <ChevronDownIcon className="h-4 w-4 text-postman-gray-medium transition-transform group-open:rotate-180" />
              </summary>
              <div className="border-t border-gray-200 bg-postman-gray-light">
                {Object.entries(testCases).map(([statusCode, result]) => (
                  <div key={statusCode} className="p-3 border-b border-gray-200 last:border-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                        Status: {statusCode}
                      </span>
                    </div>
                    <pre className="bg-white font-mono p-2 rounded text-xs overflow-x-auto border border-gray-200">
                      {JSON.stringify(result.originalResponse, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </details>
          ));
        })}
      </div>
    </div>
  );
};

export default ChainTestResults;
